// messagemonitor.js

import React, { useEffect, useMemo, useState } from "react";
import { useWebSocket } from "../contexts/WebSocketContext.js";
import { useDatabase } from "../contexts/DatabaseContext.js";
import MessageGroup from "./MessageGroup.js";
import '../css/MessageMonitor.css';
import { normalizeEpochMillis, parseTransactTimeToEpochMillis } from "../utils/time.js";

const toSortableTimestamp = (message) => {
  const rawValue = message?.messageTime ?? message?.timestamp;
  const parsedValue = normalizeEpochMillis(rawValue);
  if (Number.isFinite(parsedValue) && parsedValue > 0) {
    return parsedValue;
  }

  return parseTransactTimeToEpochMillis(message?.transactTime);
};

const sortMessagesNewestFirst = (messages) => (
  [...messages].sort((left, right) => toSortableTimestamp(right) - toSortableTimestamp(left))
);

const MessageSearchControls = () => {
  const { searchTerm, setSearchTerm, searchMessages, loadRecentMessages } = useDatabase();
  const [pendingValue, setPendingValue] = useState(searchTerm);

  useEffect(() => {
    setPendingValue(searchTerm);
  }, [searchTerm]);

  const onSearch = async () => {
    await searchMessages(pendingValue);
  };

  const onClear = async () => {
    setPendingValue('');
    setSearchTerm('');
    await loadRecentMessages();
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    await onSearch();
  };

  return (
    <form className="message-monitor-controls" onSubmit={onSubmit}>
      <input
        type="text"
        placeholder="Search monitor history"
        value={pendingValue}
        onChange={(event) => setPendingValue(event.target.value)}
      />
      <button className="message-monitor-button" type="submit">Search</button>
      <button className="message-monitor-button" type="button" onClick={onClear}>Clear</button>
    </form>
  );
};

const MessageMonitor = ({ amplifyUsername, kycComplete }) => {
  const webSocketMessages = useWebSocket();
  const { messages: databaseMessages, searchTerm } = useDatabase();

  console.log('amplifyUsername:', amplifyUsername);
  console.log('kycComplete:', kycComplete);

  const sortedGroups = useMemo(() => {
    const groupedMessages = [...databaseMessages, ...webSocketMessages].reduce((acc, message) => {
      const groupKey = message.quoteRequestID || message.timestamp || message.messageTime;
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(message);
      return acc;
    }, {});

    return Object.entries(groupedMessages)
      .map(([quoteRequestID, messages]) => {
        const sortedMessages = sortMessagesNewestFirst(messages);
        return {
          quoteRequestID,
          messages: sortedMessages,
          latestTimestamp: toSortableTimestamp(sortedMessages[0]),
        };
      })
      .sort((left, right) => right.latestTimestamp - left.latestTimestamp);
  }, [databaseMessages, webSocketMessages]);

  return (
    <div className="message-monitor-container">
      {sortedGroups.map(({ quoteRequestID, messages }) => (
        <MessageGroup
          key={quoteRequestID}
          quoteRequestID={quoteRequestID}
          messages={messages}
          searchTerm={searchTerm}
        />
      ))}
    </div>
  );
};

export default MessageMonitor;
export { MessageSearchControls };
